import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuizStudent } from "@/modes/quiz/QuizStudent";
import { QuizTeacher } from "@/modes/quiz/QuizTeacher";
import { Final, TeamScores } from "@/modes/quiz/QuizBoard";

const mmss = (ms) => { const s = Math.ceil(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

const Progress = ({ p, big }) => p ? (
  <span className={big ? "text-[2.2vh]" : "text-sm"}>{p.done}/{p.total} · %{p.accuracy} doğru{p.finished_s != null ? ` · ${p.finished_s} sn'de bitti` : ""}</span>
) : null;

export const SortStudent = ({ state, send, remaining }) => {
  if (state.phase !== "activity") return <QuizStudent state={state} send={send} remaining={remaining} />;
  const h = state.holding;
  const last = state.last;
  return (
    <div className="space-y-5" data-testid="student-sort">
      <div className="flex items-center justify-between">
        <span className="eyebrow">{state.template === "matching" ? "Eşleştirme" : "Sınıflandırma"}</span>
        <span className="font-display text-3xl font-black text-[#FACC15]" data-testid="student-timer">{mmss(remaining)}</span>
      </div>
      {last && (
        <p className={`flex items-center gap-2 text-sm ${last.ok ? "text-[#6EE7B7]" : "text-red-300"}`} data-testid="sort-last">
          {last.ok ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}{last.text} → {last.ok ? "doğru" : `doğrusu: ${last.correct}`}
        </p>
      )}
      {h ? (
        <div className="space-y-4">
          <div className="panel p-5 text-center"><p className="eyebrow">Senin kartın</p><p className="font-display text-3xl font-black mt-1" data-testid="sort-holding">{h.text}</p></div>
          <p className="text-slate-300">{state.template === "matching" ? "Eşini seç:" : "Grubunu seç:"}</p>
          <div className="grid grid-cols-2 gap-2">
            {state.targets.map((t) => (
              <button key={t.id} onClick={() => send({ type: "place", target: t.id })} data-testid={`sort-target-${t.id}`}
                className="rounded-xl border-2 border-[#FACC15]/60 bg-white/5 p-3 text-base font-bold hover:bg-[#FACC15]/15">{t.text}</button>
            ))}
          </div>
        </div>
      ) : state.can_pull ? (
        <Button onClick={() => send({ type: "pull" })} className="w-full h-16 text-xl font-bold" data-testid="sort-pull">Sıradaki kartı al</Button>
      ) : <p className="text-center text-slate-300 py-6" data-testid="sort-wait">Takımının kartları bitti. Tahtadan izle!</p>}
      <p className="text-sm text-slate-400">Takımın: <Progress p={state.team_progress} /></p>
    </div>
  );
};

export const SortBoard = ({ state, remaining }) => {
  if (state.phase === "finished") return <Final state={state} />;
  const color = (id) => state.teams.find((t) => t.id === id)?.color;
  return (
    <div className="h-full flex flex-col gap-[2vh]" data-testid="board-sort">
      <div className="flex items-center gap-6">
        <h1 className="font-display text-[min(3rem,5vh)] font-bold flex-1">{state.activity_title}</h1>
        <span className={`font-display text-[min(4.5rem,8vh)] font-bold ${remaining <= 10000 ? "text-red-400" : ""}`} data-testid="board-timer">{mmss(remaining)}</span>
      </div>
      <div className="flex flex-wrap gap-[1vw]">
        {state.targets.map((t) => <span key={t.id} className="rounded-xl border-2 border-dashed border-[#FACC15]/70 px-[1vw] py-[0.8vh] font-display text-[min(2rem,3.2vh)] font-bold text-[#FDE047]">{t.text}</span>)}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-wrap content-start gap-[0.8vw]" data-testid="board-sort-items">
        {state.items.map((i) => (
          <span key={i.id} className="rounded-xl bg-white/5 border border-slate-500/40 px-[0.9vw] py-[0.7vh] text-[min(1.6rem,2.6vh)] font-semibold flex items-center gap-2">
            {i.text}{(state.placed?.[i.id] || []).map((tid) => <span key={tid} className="h-[1.4vh] w-[1.4vh] rounded-full" style={{ backgroundColor: color(tid) }} />)}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-[1.5vw]" data-testid="board-sort-progress">
        {state.teams.map((t) => (
          <div key={t.id} className="panel px-[1.2vw] py-[1vh] border" style={{ borderColor: `${t.color}88` }}>
            <p className="font-display text-[2.4vh] font-bold" style={{ color: t.color }}>{t.name}</p>
            <Progress p={state.progress?.[t.id]} big />
          </div>
        ))}
      </div>
    </div>
  );
};

export const SortTeacher = ({ state, send }) => (
  <div className="space-y-6">
    {state.progress && (
      <div className="panel p-5 space-y-2" data-testid="teacher-sort-progress">
        <h3 className="font-display text-xl font-bold">Takım ilerlemesi</h3>
        {state.teams.map((t) => <p key={t.id} className="text-sm"><b style={{ color: t.color }}>{t.name}:</b> <Progress p={state.progress[t.id]} /></p>)}
      </div>
    )}
    {state.phase === "finished" && <TeamScores state={state} />}
    <QuizTeacher state={state} send={send} />
  </div>
);
